/**
 * Maps resolved MegaMenu Data JSON to MegaMenuProps.
 *
 * The input structure is the CmsMegaMenuPayload contract (navGroups[], a panels map keyed by
 * panelRef, inline panel), resolved by resolveAllPanels: panelRef wins over an inline panel, a
 * missing ref falls back to the inline panel with a warning, and an invalid layout renders its
 * valid subset. Every field uses its MegaMenuProps name. MegaMenuData calls this in PolyR SSR
 * and again in browser hydration, so both render the same props from the same Data.
 *
 * An authored URL that fails isNavigableUrl is treated as absent: a link or footer item that
 * needs it is dropped, a direct-link group without one is dropped, and a URL scalar is omitted.
 *
 * A nav group with no id or title is dropped, the same rule columns and links follow, and so is a
 * group whose id repeats an earlier one (the first authored group wins).
 *
 * Pure and synchronous. Malformed Data never throws: invalid elements are dropped, with a
 * console.warn where an author's group, column or link is lost.
 */

import { resolveAllPanels } from './PanelResolver';
import type { CmsColumn, CmsNavGroup, CmsPanel } from './PanelResolver';
import type { MegaMenuProps, NavFooterItem, NavLink, PrimaryNavGroup, SecondaryNavGroup } from './MegaMenu';
import {
  coerceBooleanKeys,
  filterValidElements,
  isValidDataObject,
  omitNonStrings,
  omitUnsafeUrls,
  pickKnownKeys,
} from '../../utils/resolverHelpers';

/** Root MegaMenuProps scalars, copied from Data under their own names (MegaMenu.tsx:201-337). */
const ROOT_SCALAR_KEYS: ReadonlyArray<keyof MegaMenuProps> = [
  'siteWideBannerIconUrl',
  'siteWideBannerText',
  'siteWideBannerLinkText',
  'siteWideBannerLinkUrl',
  'siteWideBannerCtaId',
  'siteWideBannerLdAppendix',
  'logoImageUrl',
  'logoUrl',
  'enableSearch',
  'searchPlaceholder',
  'searchResultsPath',
  'searchAutocompleteModule',
  'searchQuickLinks',
  'searchQuickLinksLabel',
  'mobileMenuAriaLabel',
  'enableLanguagePicker',
  'languagePickerCurrentLanguage',
  'languagePickerLanguages',
  'languagePickerHeading',
  'languagePickerBackText',
  'languagePickerCountryFormat',
  'languagePickerAddPathLocale',
  'primaryCtaText',
  'primaryCtaUrl',
  'primaryCtaId',
  'primaryCtaLdAppendix',
  'primaryCtaBehavior',
  'primaryCtaAction',
  'primaryCtaPopupId',
  'secondaryCtaText',
  'secondaryCtaUrl',
  'secondaryCtaId',
  'secondaryCtaLdAppendix',
  'secondaryCtaBehavior',
  'secondaryCtaAction',
  'secondaryCtaPopupId',
  'secondaryCtaIconName',
];

/** PrimaryNavGroup fields copied from a Data nav group; secondaryNavGroups is built separately. */
const NAV_GROUP_KEYS: ReadonlyArray<keyof PrimaryNavGroup> = [
  'title',
  'id',
  'isDirectLink',
  'directLinkUrl',
  'directLinkCtaId',
  'hasFeaturedPanel',
  'featuredImageUrl',
  'featuredImageAlt',
  'featuredUrl',
  'featuredId',
  'featuredTitle',
  'featuredDescription',
  'featuredBadgeText',
  'featuredBadgeIconUrl',
  'featuredBadgeIconAlt',
  'featuredVideoUrl',
  'featuredPanelTitle',
  'featuredSize',
];

/** SecondaryNavGroup fields copied from a Data column; columnType and links are built separately. */
const COLUMN_KEYS: ReadonlyArray<keyof SecondaryNavGroup> = [
  'title',
  'variant',
  'actionText',
  'actionUrl',
  'actionCtaId',
];

/** NavLink fields copied from a Data link. */
const LINK_KEYS: ReadonlyArray<keyof NavLink> = [
  'title',
  'url',
  'id',
  'iconUrl',
  'iconAlt',
  'badgeText',
  'description',
  'align',
];

/** NavFooterItem fields copied from a Data footer item. */
const FOOTER_ITEM_KEYS: ReadonlyArray<keyof NavFooterItem> = ['id', 'title', 'url', 'target', 'iconUrl', 'text'];

/** Keys holding an authored href, per level. */
const ROOT_URL_KEYS: ReadonlyArray<string> = [
  'logoUrl',
  'siteWideBannerLinkUrl',
  'primaryCtaUrl',
  'secondaryCtaUrl',
  'searchResultsPath',
];
const NAV_GROUP_URL_KEYS: ReadonlyArray<string> = ['directLinkUrl', 'featuredUrl', 'featuredVideoUrl'];
const COLUMN_URL_KEYS: ReadonlyArray<string> = ['actionUrl'];
const LINK_URL_KEYS: ReadonlyArray<string> = ['url'];

/** JSON-string list props whose rows are checked before MegaMenu parses them (LanguagePicker.tsx Language, SearchBar quick links). */
const JSON_ROW_FIELDS: ReadonlyArray<{
  key: 'searchQuickLinks' | 'languagePickerLanguages';
  stringKeys: ReadonlyArray<string>;
  urlKeys: ReadonlyArray<string>;
  optionalStringKeys: ReadonlyArray<string>;
}> = [
  { key: 'searchQuickLinks', stringKeys: ['text'], urlKeys: ['href'], optionalStringKeys: [] },
  { key: 'languagePickerLanguages', stringKeys: ['code', 'name'], urlKeys: [], optionalStringKeys: ['bcp47'] },
];

/** Keys whose MegaMenuProps type is boolean, per level. */
const ROOT_BOOLEAN_KEYS: ReadonlyArray<string> = ['enableSearch', 'enableLanguagePicker', 'languagePickerAddPathLocale'];
const NAV_GROUP_BOOLEAN_KEYS: ReadonlyArray<string> = ['isDirectLink', 'hasFeaturedPanel'];

/** Keys whose MegaMenuProps type is a string (or string union), per level: every copied key that is not a boolean. */
const ROOT_STRING_KEYS: ReadonlyArray<string> = ROOT_SCALAR_KEYS.filter((key) => !ROOT_BOOLEAN_KEYS.includes(key));
const NAV_GROUP_STRING_KEYS: ReadonlyArray<string> = NAV_GROUP_KEYS.filter((key) => !NAV_GROUP_BOOLEAN_KEYS.includes(key));
const COLUMN_STRING_KEYS: ReadonlyArray<string> = COLUMN_KEYS;
const LINK_STRING_KEYS: ReadonlyArray<string> = LINK_KEYS;
const FOOTER_ITEM_STRING_KEYS: ReadonlyArray<string> = FOOTER_ITEM_KEYS;

const COLUMN_REQUIRED_KEYS: (keyof SecondaryNavGroup)[] = ['title'];
const LINK_REQUIRED_KEYS: (keyof NavLink)[] = ['title', 'url'];
const NAV_GROUP_REQUIRED_KEYS: (keyof PrimaryNavGroup)[] = ['id', 'title'];

/** A featured column only shapes layout validation; its content comes from the nav group's featured* fields. */
const FEATURED_COLUMN_TYPE = 'featured';

function hasRequiredKeys<T>(element: unknown, requiredKeys: (keyof T)[]): boolean {
  return filterValidElements<T>([element], requiredKeys).length === 1;
}

function groupNameOf(group: Record<string, unknown>): string {
  // PanelResolver names a group by `label` in its warnings; Data names it by `title`.
  const name = group.label ?? group.title;
  return typeof name === 'string' ? name : '';
}

function toCmsPanel(value: unknown): CmsPanel | undefined {
  if (!isValidDataObject(value)) {
    return undefined;
  }
  const columns = Array.isArray(value.columns)
    ? (value.columns.filter(isValidDataObject) as CmsColumn[])
    : [];
  const templateId = typeof value.templateId === 'string' ? value.templateId : undefined;
  return { templateId, columns };
}

function toPanelsMap(value: unknown): Record<string, CmsPanel> | undefined {
  if (!isValidDataObject(value)) {
    return undefined;
  }
  // No prototype, so a panelRef such as "toString" is a missing ref, not an inherited value.
  const panels: Record<string, CmsPanel> = Object.create(null);
  for (const [key, entry] of Object.entries(value)) {
    const panel = toCmsPanel(entry);
    if (panel) {
      panels[key] = panel;
    }
  }
  return panels;
}

function toCmsNavGroup(group: Record<string, unknown>): CmsNavGroup {
  return {
    label: groupNameOf(group),
    panel: toCmsPanel(group.panel),
    panelRef: typeof group.panelRef === 'string' ? group.panelRef : undefined,
  };
}

/** A direct-link group with no navigable directLinkUrl, absent or failing, has no destination. */
function hasNoDirectLinkDestination(safeGroup: Record<string, unknown>): boolean {
  return safeGroup.isDirectLink === true && safeGroup.directLinkUrl === undefined;
}

function toSafeNavGroups(groups: Record<string, unknown>[]): Record<string, unknown>[] {
  const safeGroups: Record<string, unknown>[] = [];
  const seenIds = new Set<string>();
  for (const group of groups) {
    const safeGroup = omitUnsafeUrls(
      coerceBooleanKeys(omitNonStrings(group, NAV_GROUP_STRING_KEYS), NAV_GROUP_BOOLEAN_KEYS),
      NAV_GROUP_URL_KEYS,
    );
    if (!hasRequiredKeys<PrimaryNavGroup>(safeGroup, NAV_GROUP_REQUIRED_KEYS)) {
      console.warn('[MegaMenu] Nav group with no id or title. Dropping the group.');
      continue;
    }
    if (hasNoDirectLinkDestination(safeGroup)) {
      console.warn(`[MegaMenu] Direct-link nav group "${groupNameOf(group)}" has no navigable directLinkUrl. Dropping the group.`);
      continue;
    }
    if (seenIds.has(safeGroup.id as string)) {
      console.warn(`[MegaMenu] Nav group "${groupNameOf(group)}" repeats id "${safeGroup.id}". Dropping the group.`);
      continue;
    }
    seenIds.add(safeGroup.id as string);
    safeGroups.push(safeGroup);
  }
  return safeGroups;
}

function toNavLinks(links: unknown, groupName: string): NavLink[] {
  if (!Array.isArray(links)) {
    return [];
  }
  const navLinks: NavLink[] = [];
  for (const link of links) {
    const safeLink = isValidDataObject(link)
      ? omitUnsafeUrls(omitNonStrings(link, LINK_STRING_KEYS), LINK_URL_KEYS)
      : undefined;
    if (!safeLink || !hasRequiredKeys<NavLink>(safeLink, LINK_REQUIRED_KEYS)) {
      console.warn(`[MegaMenu] Link with no title or url in nav group "${groupName}". Dropping the link.`);
      continue;
    }
    navLinks.push(pickKnownKeys<NavLink>(safeLink, LINK_KEYS) as NavLink);
  }
  return navLinks;
}

function toSecondaryNavGroups(panel: CmsPanel | undefined, groupName: string): SecondaryNavGroup[] {
  if (!panel) {
    return [];
  }
  const secondaryNavGroups: SecondaryNavGroup[] = [];
  for (const panelColumn of panel.columns) {
    const column = omitNonStrings(panelColumn as Record<string, unknown>, COLUMN_STRING_KEYS) as CmsColumn;
    if (column.type === FEATURED_COLUMN_TYPE) {
      continue;
    }
    if (column.type !== 'single' && column.type !== 'dual') {
      const columnType: unknown = column.type;
      const shown = typeof columnType === 'string' ? columnType : typeof columnType;
      console.warn(`[MegaMenu] Column type "${shown}" in nav group "${groupName}" is not single, dual or featured. Dropping the column.`);
      continue;
    }
    if (!hasRequiredKeys<SecondaryNavGroup>(column, COLUMN_REQUIRED_KEYS)) {
      console.warn(`[MegaMenu] Column with no title in nav group "${groupName}". Dropping the column.`);
      continue;
    }
    secondaryNavGroups.push({
      ...pickKnownKeys<SecondaryNavGroup>(omitUnsafeUrls(column, COLUMN_URL_KEYS), COLUMN_KEYS),
      columnType: column.type,
      links: toNavLinks(column.links, groupName),
    } as SecondaryNavGroup);
  }
  return secondaryNavGroups;
}

function toPrimaryNavGroup(
  group: Record<string, unknown>,
  panel: CmsPanel | undefined,
  groupName: string,
): PrimaryNavGroup {
  return {
    ...pickKnownKeys<PrimaryNavGroup>(group, NAV_GROUP_KEYS),
    secondaryNavGroups: toSecondaryNavGroups(panel, groupName),
  } as PrimaryNavGroup;
}

function toNavFooterItems(items: unknown[]): NavFooterItem[] {
  const footerItems: NavFooterItem[] = [];
  for (const item of items) {
    if (!isValidDataObject(item)) {
      continue;
    }
    const safeItem = omitUnsafeUrls(omitNonStrings(item, FOOTER_ITEM_STRING_KEYS), LINK_URL_KEYS);
    // Only a URL that was authored and failed drops the item; an item with no url passes through.
    if (item.url !== undefined && safeItem.url === undefined) {
      console.warn('[MegaMenu] Link with no title or url in navFooterItems. Dropping the link.');
      continue;
    }
    footerItems.push(pickKnownKeys<NavFooterItem>(safeItem, FOOTER_ITEM_KEYS) as NavFooterItem);
  }
  return footerItems;
}

/**
 * Returns a JSON row holding only the fields MegaMenu reads, or undefined when a required text field
 * is not a string or a listed href is not navigable. An optional text field is kept only when it is
 * a string, because MegaMenu renders it as a React child.
 */
function toSafeJsonRow(
  row: unknown,
  stringKeys: ReadonlyArray<string>,
  urlKeys: ReadonlyArray<string>,
  optionalStringKeys: ReadonlyArray<string>,
): Record<string, unknown> | undefined {
  if (!isValidDataObject(row)) {
    return undefined;
  }
  const safeRow = omitUnsafeUrls(omitNonStrings(row, [...stringKeys, ...optionalStringKeys]), urlKeys);
  if (!stringKeys.every((key) => typeof safeRow[key] === 'string') || !urlKeys.every((key) => safeRow[key] !== undefined)) {
    return undefined;
  }
  return pickKnownKeys<Record<string, unknown>>(safeRow, [...stringKeys, ...urlKeys, ...optionalStringKeys]);
}

/**
 * Keeps the rows of a JSON-string list prop that MegaMenu can render safely and re-serializes the
 * cleaned rows.
 * A string that is not JSON passes through unchanged, because MegaMenu warns when it fails to parse
 * the same string. A parsed value that is not an array returns undefined, so the caller omits the prop.
 */
function toSafeJsonRows(
  value: string,
  stringKeys: ReadonlyArray<string>,
  urlKeys: ReadonlyArray<string>,
  optionalStringKeys: ReadonlyArray<string>,
  fieldName: string,
): string | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (!Array.isArray(parsed)) {
    console.warn(`[MegaMenu] ${fieldName} is not a JSON array. Omitting it.`);
    return undefined;
  }
  const safeRows: Record<string, unknown>[] = [];
  for (const row of parsed) {
    const safeRow = toSafeJsonRow(row, stringKeys, urlKeys, optionalStringKeys);
    if (safeRow) {
      safeRows.push(safeRow);
    }
  }
  return JSON.stringify(safeRows);
}

/**
 * Maps resolved MegaMenu Data JSON to the props MegaMenu renders.
 *
 * @param data - Resolved MegaMenu Data JSON: `navGroups`, optional `panels` map, optional
 *   `navFooterItems`, and root scalars under their MegaMenuProps names. Any other value,
 *   including null or an array, yields a menu with no navigation groups.
 * @returns MegaMenuProps holding only known MegaMenuProps keys. `navFooterItems` is present
 *   only when the Data carries a `navFooterItems` array.
 */
export function resolveMegaMenuProps(data: unknown): MegaMenuProps {
  if (!isValidDataObject(data)) {
    return { primaryNavGroups: [] };
  }

  const groups = toSafeNavGroups(Array.isArray(data.navGroups) ? data.navGroups.filter(isValidDataObject) : []);
  const resolved = resolveAllPanels({
    navGroups: groups.map(toCmsNavGroup),
    panels: toPanelsMap(data.panels),
  });

  const props: MegaMenuProps = {
    ...pickKnownKeys<MegaMenuProps>(
      coerceBooleanKeys(omitUnsafeUrls(omitNonStrings(data, ROOT_STRING_KEYS), ROOT_URL_KEYS), ROOT_BOOLEAN_KEYS),
      ROOT_SCALAR_KEYS,
    ),
    // resolveAllPanels maps navGroups in order, so index i is the same Data group.
    primaryNavGroups: resolved.navGroups.map((cmsGroup, index) =>
      toPrimaryNavGroup(groups[index], cmsGroup.panel, cmsGroup.label)),
  };

  for (const { key, stringKeys, urlKeys, optionalStringKeys } of JSON_ROW_FIELDS) {
    const value = props[key];
    if (value === undefined) {
      continue;
    }
    const safeValue = toSafeJsonRows(value, stringKeys, urlKeys, optionalStringKeys, key);
    if (safeValue === undefined) {
      delete props[key];
    } else {
      props[key] = safeValue;
    }
  }

  if (Array.isArray(data.navFooterItems)) {
    props.navFooterItems = toNavFooterItems(data.navFooterItems);
  }

  return props;
}
